import z from 'zod'
import { ApiSecretRequiredException } from '../../errors'
import type { AssetBalance, GetCurrentAssetsParameters } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { decimalSchema } from '../../utils/schema'
import { bitbankAccountCategory } from '../constants'
import { createClient } from '../createClient'

export const getCurrentBitbankAssets = async ({
  apiKey,
  apiSecret,
  assets,
  proxy,
  symbolAssetMap,
  createHeaders,
}: GetCurrentAssetsParameters): Promise<AssetBalance[]> => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const getAsset = createGetAsset({
    assets,
    symbolAssetMap,
  })

  const timestamp = Date.now()

  const response = await client.run({
    method: 'GET',
    path: '/v1/user/assets',
    responseSchema: z.object({
      success: z.literal(1),
      data: z.object({
        assets: z.array(assetSchema),
      }),
    }),
  })

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.data.assets) {
    const asset = getAsset(assetData.asset)
    if (asset === undefined) {
      continue
    }
    if (assetData.onhand_amount.value <= 0n) {
      continue
    }
    assetBalances.push({
      key: `${bitbankAccountCategory}_onhand`,
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount: assetData.onhand_amount,
      timestamp,
    })
  }

  return assetBalances
}

const assetSchema = z.object({
  /** アセット名 */
  asset: z.string(),
  /** 利用可能な量 */
  free_amount: decimalSchema,
  /** 精度 */
  amount_precision: z.number().int(),
  /** 保有量 */
  onhand_amount: decimalSchema,
  /** ロックされている量 */
  locked_amount: decimalSchema,
  /** ロックされている量のうち出金中数量 */
  withdrawing_amount: decimalSchema,
})
